import React from 'react';
import { School, BookOpen, Users, Sprout, GraduationCap, Heart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

export const CommunityApproach = () => {
  const communityFeatures = [
    {
      icon: School,
      title: 'Community Learning Hub',
      description: 'Extended hours offering classes for parents and families: Parenting a Teen, Beginning English, CPR, money management, and GED programs.',
      gradient: 'from-tealAccent/10 to-tealAccent/5'
    },
    {
      icon: Users,
      title: 'Family Partnerships',
      description: 'Monthly workshops, parent advisory council, volunteer opportunities, and strong home-school connections.',
      gradient: 'from-goldenOlive/10 to-goldenOlive/5'
    },
    {
      icon: Sprout,
      title: 'Community Garden',
      description: 'Hands-on learning through our campus gardens where students, families, and community members grow together.',
      gradient: 'from-sageGreen/10 to-sageGreen/5'
    },
    {
      icon: Heart,
      title: 'Local Partnerships',
      description: 'Collaborations with businesses, universities, and nonprofits to provide real-world learning opportunities.',
      gradient: 'from-coralRed/10 to-coralRed/5'
    },
    {
      icon: BookOpen,
      title: 'Cultural Responsiveness',
      description: 'Deep commitment to honoring and integrating the diverse cultural traditions of our Inland Empire community.',
      gradient: 'from-navyBlue/10 to-navyBlue/5'
    },
    {
      icon: GraduationCap,
      title: 'Community Service',
      description: 'Students engage in meaningful service projects, building leadership, empathy, and connection to their community.',
      gradient: 'from-tealAccent/10 to-goldenOlive/5'
    }
  ];

  const firstThreeFeatures = communityFeatures.slice(0, 3);
  const lastThreeFeatures = communityFeatures.slice(3, 6);

  return (
    <section id="community" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <div className="inline-block bg-sageGreen/10 px-6 py-2 rounded-full mb-4">
            <span className="text-sageGreen font-semibold">Community at Our Core</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">A School That Serves Beyond the Classroom</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Doctrina Charter School is more than a place of learning—it's a <span className="font-semibold text-sageGreen">community hub</span> where families, educators, and neighbors come together to nurture the next generation.
          </p>
        </div>

        {/* Parent Classes Image - Community Learning Hub */}
        <div className="mb-16 relative rounded-3xl overflow-hidden shadow-2xl max-h-[400px]">
          <img 
            src={process.env.REACT_APP_PARENT_CLASSES_URL} 
            alt="Parents and families attending educational classes" 
            className="w-full h-full object-cover"
            style={{maxHeight: '400px'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-tealAccent/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3">Community Learning Hub: Education for All</h3>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
              Extended hours offering classes for parents and families including Parenting a Teen, Beginning English, CPR, money management, and GED programs—strengthening our entire community through learning.
            </p>
          </div>
        </div>

        {/* First Three Feature Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {firstThreeFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index} className={`border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br ${feature.gradient} group overflow-hidden relative`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/30 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardHeader className="relative z-10">
                  <div className="bg-white/80 backdrop-blur-sm w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                    <Icon className="text-navyBlue" size={28} />
                  </div>
                  <CardTitle className="text-xl text-navyBlue">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-gray-700 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Student Life Image - Lunch in Courtyard */}
        <div className="mb-16 relative rounded-3xl overflow-hidden shadow-2xl max-h-[400px]">
          <img 
            src={process.env.REACT_APP_STUDENT_LUNCH_URL} 
            alt="Students enjoying lunch in courtyard" 
            className="w-full h-full object-cover"
            style={{maxHeight: '400px'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navyBlue/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3">Building Community, One Meal at a Time</h3>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
              Our beautiful courtyards serve as gathering spaces where students connect, collaborate, and build lasting friendships over shared meals.
            </p>
          </div>
        </div>

        {/* Last Three Feature Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {lastThreeFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index} className={`border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br ${feature.gradient} group overflow-hidden relative`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/30 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <CardHeader className="relative z-10">
                  <div className="bg-white/80 backdrop-blur-sm w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                    <Icon className="text-navyBlue" size={28} />
                  </div>
                  <CardTitle className="text-xl text-navyBlue">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-gray-700 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Community Garden Image */}
        <div className="mb-16 relative rounded-3xl overflow-hidden shadow-2xl max-h-[400px]">
          <img 
            src={process.env.REACT_APP_COMMUNITY_GARDEN_URL} 
            alt="Students learning through hands-on gardening" 
            className="w-full h-full object-cover"
            style={{maxHeight: '400px'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-sageGreen/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3">Growing Together: Our Community Garden</h3>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
              Students, families, and community members cultivate more than just plants—they grow responsibility, sustainability, and connections through hands-on learning in our thriving campus gardens.
            </p>
          </div>
        </div>

        {/* Community Impact Stats */}
        <div className="bg-gradient-to-br from-navyBlue to-navyBlue/90 rounded-2xl p-8 sm:p-12 text-white">
          <h3 className="text-3xl font-bold text-center mb-12">Serving the Inland Empire</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-tealAccent mb-2">4</div>
              <p className="text-white/90">School Districts Served</p>
              <p className="text-sm text-white/70 mt-1">Romoland, Homeland, Menifee, Winchester</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-goldenOlive mb-2">100%</div>
              <p className="text-white/90">Family Engagement</p>
              <p className="text-sm text-white/70 mt-1">Monthly workshops & events</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-coralRed mb-2">38</div>
              <p className="text-white/90">Acre Campus</p>
              <p className="text-sm text-white/70 mt-1">Gardens, classrooms, community spaces</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-sageGreen mb-2">1:1</div>
              <p className="text-white/90">Technology</p>
              <p className="text-sm text-white/70 mt-1">Chromebooks for every student</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunityApproach;
